import { BehaviorSubject } from 'rxjs';

// Import `globalCache` from the direct file path rather than the
// `@sbrc/utils` barrel. The barrel transitively pulls in many unrelated
// helpers (window.location-reading apiEndpoint, dashboard reducers,
// etc.) that some test environments (notably the `PrivatePage` integration
// test) cannot load cleanly. globalCache is dependency-free so a direct
// import is both lighter and more honest about what we depend on.
import { globalCache } from '@sbrc/utils/globalCache';

/**
 * BehaviorSubject-backed store for the current authenticated user ID.
 *
 * Split out from `currentUserID.ts` so that callers which only need to
 * read or write the store (e.g. `invalidateSession`) do not transitively
 * import `getApp`. `currentUserID.ts` itself imports `getApp` to wire
 * reAuthenticate + error-hook side effects, which creates an
 * `invalidateSession → currentUserID → getApp → sessionLifecycle →
 * invalidateSession` cycle if `invalidateSession` consumes
 * `currentUserID()` directly. Reading the store from this module
 * avoids that cycle entirely.
 *
 * The `globalCache` key is shared with the wiring in `currentUserID.ts`
 * so both sites reference the same `BehaviorSubject`.
 */
export type UserIDStore = {
  set(value: string | null): void;
  get(): string | null | undefined;
  asObservable(): ReturnType<
    BehaviorSubject<string | null | undefined>['asObservable']
  >;
};

export function userIDStore(): UserIDStore {
  return globalCache('currentUserID', () => {
    const userID$ = new BehaviorSubject<string | null | undefined>(undefined);

    return {
      set(value: string | null) {
        userID$.next(value);
      },
      get() {
        return userID$.value;
      },
      asObservable() {
        return userID$.asObservable();
      },
    };
  });
}
